import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

type ThemeMode = 'light' | 'dark' | 'system';
type ResolvedTheme = 'light' | 'dark';

interface ThemeContextValue {
  /**
   * The brand's configured theme mode (light, dark, or system).
   */
  themeMode: ThemeMode;
  /**
   * The actual resolved theme (light or dark).
   */
  resolvedTheme: ResolvedTheme;
  /**
   * Whether user can toggle theme (only when brand allows system mode).
   */
  canToggle: boolean;
  /**
   * User's preference when brand allows system mode.
   */
  userPreference: ResolvedTheme | null;
  /**
   * Toggle between light and dark (only works when canToggle is true).
   */
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const STORAGE_KEY = 'airclou-kb-portal-theme';

// Migration: read old key and write to new key, then remove old key
function migrateThemeKey(): ResolvedTheme | null {
  try {
    const old = localStorage.getItem('kb-portal-theme');
    if (old === 'light' || old === 'dark') {
      localStorage.setItem(STORAGE_KEY, old);
      localStorage.removeItem('kb-portal-theme');
      return old;
    }
  } catch {
    // ignore
  }
  return null;
}

function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

interface ThemeProviderProps {
  children: React.ReactNode;
  themeMode: ThemeMode;
}

export function ThemeProvider({ children, themeMode }: ThemeProviderProps) {
  // User preference only matters when themeMode is 'system'
  const [userPreference, setUserPreference] = useState<ResolvedTheme | null>(() => {
    if (themeMode !== 'system') return null;
    const migrated = migrateThemeKey();
    if (migrated) return migrated;
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === 'light' || stored === 'dark' ? stored : null;
  });

  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(getSystemTheme);

  // Calculate resolved theme
  const resolvedTheme: ResolvedTheme = (() => {
    if (themeMode === 'light') return 'light';
    if (themeMode === 'dark') return 'dark';
    // system mode: user preference > system preference
    return userPreference ?? systemTheme;
  })();

  // Apply theme class to document
  useEffect(() => {
    const root = document.documentElement;

    // Tailwind uses `.dark` selector.
    if (resolvedTheme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [resolvedTheme]);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      setSystemTheme(e.matches ? 'dark' : 'light');
    };
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Reset user preference when themeMode changes
  useEffect(() => {
    if (themeMode !== 'system') {
      setUserPreference(null);
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [themeMode]);

  const toggleTheme = useCallback(() => {
    if (themeMode !== 'system') return;
    
    const newTheme = resolvedTheme === 'light' ? 'dark' : 'light';
    setUserPreference(newTheme);
    localStorage.setItem(STORAGE_KEY, newTheme);
  }, [themeMode, resolvedTheme]);

  const value: ThemeContextValue = {
    themeMode,
    resolvedTheme,
    canToggle: themeMode === 'system',
    userPreference,
    toggleTheme,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
